<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Thread } from '../services/api';
import { threadsAPI } from '../services/api';
import { isThreadTaken } from '../utils/thread';

interface Props {
  conversation: Thread;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  updateConversation: [conversation: Thread];
}>();

const { t } = useI18n();

const isLoading = ref(false);
const error = ref<string | null>(null);

const isThreadDone = computed(() => props.conversation.status === 'done');

const isTaken = computed(() => isThreadTaken(props.conversation, Date.now()));

// Format taken until date
const takenUntilFormatted = computed(() => {
  if (!props.conversation.takenUntil) return '';

  const date = new Date(props.conversation.takenUntil);
  return date.toLocaleString('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
});

const handleTakeOver = async () => {
  if (isLoading.value || isThreadDone.value) return;

  isLoading.value = true;
  error.value = null;

  try {
    let updatedThreadData;

    if (isTaken.value) {
      // Release conversation back to AI
      updatedThreadData = await threadsAPI.release(props.conversation.id);
    } else {
      // Take over conversation
      updatedThreadData = await threadsAPI.takeOver(props.conversation.id);
    }

    // Update conversation with new taken_until value
    const updatedConversation: Thread = {
      ...props.conversation,
      takenUntil: updatedThreadData.taken_until,
    };

    console.log('Updated thread data:', updatedThreadData);
    console.log('Updated conversation:', updatedConversation);
    console.log('New takenUntil:', updatedConversation.takenUntil);

    emit('updateConversation', updatedConversation);
  } catch (err: any) {
    console.error('Error updating conversation:', err);
    error.value = err.response?.data?.message || t('status_panel.error_update');
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <div class="w-64 bg-[#111113] border-l border-[#2A2018] flex flex-col">
    <!-- Header -->
    <div class="px-6 py-6 border-b border-[#2A2018]">
      <h2 class="text-xl text-[#F5F0EA]">{{ $t('status_panel.title') }}</h2>
    </div>

    <!-- Status Content -->
    <div class="flex-1 px-6 py-6 space-y-6">
      <!-- AI Active Status -->
      <div v-if="!isTaken" class="space-y-3">
        <div class="flex items-center gap-2">
          <div class="w-2 h-2 bg-green-500 rounded-full" />
          <p class="text-sm text-[#9E9080]">{{ $t('status_panel.ai_active') }}</p>
        </div>
        <p class="text-xs text-[#524840]">
          {{ $t('status_panel.ai_active_description') }}
        </p>
      </div>

      <!-- Taken by Human Status -->
      <div v-else class="space-y-3">
        <div class="flex items-center gap-2">
          <div class="w-2 h-2 bg-blue-500 rounded-full" />
          <p class="text-sm text-[#9E9080]">{{ $t('status_panel.taken') }}</p>
        </div>
        <p class="text-xs text-[#524840]">
          {{ $t('status_panel.taken_until', { date: takenUntilFormatted }) }}
        </p>
      </div>

      <!-- Needs Support Status -->
      <div v-if="conversation.status === 'needs-support'" class="space-y-3">
        <div class="flex items-center gap-2">
          <div class="w-2 h-2 bg-amber-500 rounded-full" />
          <p class="text-sm text-[#9E9080]">
            {{ $t('status_panel.needs_support') }}
          </p>
        </div>
        <p class="text-xs text-[#524840]">
          {{ $t('status_panel.needs_support_description') }}
        </p>
      </div>
    </div>

    <!-- Take Over / Release Button - Always Visible -->
    <div class="p-6 border-t border-[#2A2018] space-y-3">
      <p class="text-xs text-[#524840] leading-relaxed">
        {{ isTaken ? $t('status_panel.release_hint') : $t('status_panel.take_over_hint') }}
      </p>

      <!-- Error Message -->
      <div v-if="error" class="p-3 bg-red-900/20 border border-red-800 rounded-lg">
        <p class="text-xs text-red-400">{{ error }}</p>
      </div>

      <button
        @click="handleTakeOver"
        :disabled="isLoading || isThreadDone"
        :class="[
          'w-full px-4 py-3 rounded-2xl transition text-sm flex items-center justify-center gap-2',
          isTaken
            ? 'bg-blue-900/40 hover:bg-blue-900/60 text-blue-300 disabled:bg-blue-900/20'
            : 'bg-[#1E1E23] hover:bg-[#2A2018] text-[#9E9080] disabled:bg-[#111113]',
          isLoading || isThreadDone ? 'opacity-50 cursor-not-allowed' : '',
        ]"
      >
        <div
          v-if="isLoading"
          class="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"
        ></div>
        <span>{{
          isLoading
            ? $t('status_panel.processing')
            : isTaken
              ? $t('status_panel.release_button')
              : $t('status_panel.take_over_button')
        }}</span>
      </button>
    </div>
  </div>
</template>
